import { IonSpinner } from "@ionic/react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import OrderService from "../../../services/OrderService";
import { NavLink } from "react-router-dom";
import withNav from "../../../utils/hoc/withNav";
import Page from "../../../components/Page";
import ATHS from "../../../components/ATHS";
import { OrderStatuses, TaskStatuses, orderStatusMap } from "../../../config";
import { SPECIAL_ERRORS } from "../../../config/error";
import ErrorComponent from "../../../components/Error";
import useIsAppOffline from "../../../utils/hooks/useIsOffline";
import { TrackOrderProps } from "../../../types/trackOrderProps";
import OrderItem from "./components/OrderItem";
import { isEmptyObject } from "../../../utils/objects";
import { debounce } from "../../../utils/misc";
import CollectionModal from "./components/CollectionModal";
import { PrimaryTextLight } from "../../../components/Texts";
import { ORDER_IN_PROGRESS_MESSAGE, ORDER_IN_QUEUE_MESSAGE } from "../../../config/messages";
import ActiveOrderMessage from "../components/ActiveOrderMessage";
import { ModalOverlay } from "../../../components/Modal";

const PollingPeriods: any = {
	[OrderStatuses.CREATED]: {
		pollingPeriod: 25 * 1000,
	},
	[OrderStatuses.IN_QUEUE]: {
		pollingPeriod: 25 * 1000,
	},
	[OrderStatuses.IN_PROGRESS]: {
		pollingPeriod: 10 * 1000,
		dependentonItemStatuses: true,
		itemStatusPollingPeriods: {
			[TaskStatuses.READY_TO_COLLECT]: 10 * 1000,
			[TaskStatuses.COLLECTING]: 5 * 1000,
			[TaskStatuses.COLLECT_INITIATED]: 5 * 1000,
		},
	},
	DEFAULT: {
		pollingPeriod: 25 * 1000,
	},
};

const OrderItems = styled.div`
	display: flex;
	flex-direction: column;
	height: max-content;
	width: 100%;
`;

const Center = styled.div`
	display: flex;
	flex-direction: column;
	text-align: center;
	align-items: center;
	justify-content: center;
	height: 100%;
	width: 100%;
`;

const PageContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: max-content;
	width: 100%;
	padding: 0 1rem;
`;

const OrderDetails = styled.div<any>`
	display: flex;
	gap: 0.5rem;
	width: 100%;
	margin: 1rem 0;
	${(props) =>
		props.terminalState &&
		css`
			margin: 0.2rem 0 0 0;
			gap: 0;
			width: 100%;
			justify-content: center;
			align-items: center;
		`}
`;

const KioskDetailsContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
`;

const OrderId = styled.div`
	font-weight: 700;
	font-size: 18px;
	color: #f2f2f2;
`;

const KioskDetails = styled.div<any>`
	display: block;
	overflow: hidden;
	font-weight: ${({ subText }) => (subText ? 400 : 600)};
	font-size: 13px;
	color: ${({ subText }) => (subText ? "#d6d6d6" : "#ffffff")};
	align-self: flex-start;
	text-align: left;
`;

const CartItemsCard = styled.div`
	background: #4B4B4B;
	height: max-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	border-radius: 6px;
	margin: 1rem 0 0 0;
	padding: 0 1rem;
`;

const CenteredContainer = styled.div`
	margin: 2rem 0 0 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 100%;
	width: 100%;
`;

const StatusHead = styled.div<any>`
	font-weight: 500;
	font-size: 22px;
	color: ${(props) => props.color};
`;

const BigSpace = styled.div`
	margin: 4rem 0 0 0;
`;

const SmallerSpace = styled.div`
	margin: 1rem 0 0 0;
`;

const Icon = styled.svg`
	width: 175px;
	height: 175px;
	align-self: center;
	margin: 2rem 0 0 0;
`;

const Message = styled.div`
	width: 70%;
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
`;

const SmallMessage = styled.div`
	font-size: 10px;
	font-weight: 400;
	color: #d6d6d6;
	width: 70%;
`;

const Link = styled(NavLink)`
	color: #f2f2f2;
	text-decoration: underline;
	font-weight: 500;
	font-size: 20px;
`;

const BrandLogo = styled.div`
	display: flex;
	align-self: flex-start;
`;

const BrandLogoImg = styled.img`
	width: 35px;
	height: 35px;
	border-radius: 4px;
`;

const maxRequestErrorsAllowed = 3;

const TrackOrder = (props: TrackOrderProps) => {
	const { kioskId, initialOrderData, orderId, dietaryPreferences, uuid, errorMetaDataHandler } = props;

	const isAppOffline = useIsAppOffline();

	const [orderData, setOrderData] = useState<any>(initialOrderData || {});
	const [error, setError] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [errorCode, setErrorCode] = useState<any>();
	const [updateDataCallLoading, setUpdateDataCallLoading] = useState<boolean>(false);
	const [showOverlay, toggleOverlay] = useState<boolean>(false);

	//Using Refs for polling as we don't want to cause any unnecessary renders due to it.

	const pollingId = useRef<any>(0);
	const pollingPeriod = useRef<number>(0);
	const consecutiveErrors = useRef<number>(0);

	useEffect(() => {
		return () => {
			stopPolling();
		};
	}, []);

	useEffect(() => {
		if (isAppOffline) {
			setErrorCode(SPECIAL_ERRORS.NETWORK_ERROR);
			setError(true);
		}
	}, [isAppOffline]);

	useEffect(() => {
		if (isEmptyObject(orderData)) return;
		//Check for current order Status.
		const { status, orderItems } = orderData;
		if (!(status in orderStatusMap)) {
			stopPolling();
			setError(true);
			return;
		}
		handlePolling(status, orderItems);
	}, [orderData]);

	useLayoutEffect(() => {
		if (!orderData) return;

		const { status } = orderData;

		if (status === OrderStatuses.ABORTED || status === OrderStatuses.COMPLETED || status === OrderStatuses.PARTIALLY_COMPLETED) {
			const script = document.createElement("script");
			script.src = "https://embed.typeform.com/next/embed.js";
			script.async = true;
			script.id = "form-script";
			document.body.appendChild(script);
		} else {
			const scriptElement = document.getElementById("form-script");
			if (scriptElement) {
				document.body.removeChild(scriptElement);
			}
		}

		return () => {
			const scriptElement = document.getElementById("form-script");
			if (scriptElement) {
				document.body.removeChild(scriptElement);
			}
		};
	}, [orderData]);

	useEffect(() => {	
		toggleOverlay(updateDataCallLoading);
	}, [updateDataCallLoading])
 
	/* Polling Functions */

	function startPolling() {
		pollingId.current = setInterval(getOrderData, pollingPeriod.current);
	}

	function updatePollingPeriod() {
		clearTimeout(pollingId.current);
		pollingId.current = setInterval(getOrderData, pollingPeriod.current);
	}

	function stopPolling() {
		clearTimeout(pollingId.current);
		pollingId.current = "";
	}

	function checkForActiveItems(status: string, items: any) {
		let minPollingPeriod: number | null = null;
		for (const item of items) {
			const itemStatusPollingPeriod = PollingPeriods[status]?.itemStatusPollingPeriods?.[item.status];
			if (itemStatusPollingPeriod !== undefined) {
				if (minPollingPeriod === null || itemStatusPollingPeriod < minPollingPeriod) {
					minPollingPeriod = itemStatusPollingPeriod;
				}
			}
		}
		return minPollingPeriod !== null ? [true, minPollingPeriod] : [false];
	}

	function handlePolling(status: string, orderItems: any) {
		//Handle Polling
		if (!PollingPeriods?.[status]) {
			if (pollingId.current) stopPolling();
			return;
		}
		let { pollingPeriod: newPollingPeriod, dependentonItemStatuses } = PollingPeriods[status];
		let currentPollingPeriod = pollingPeriod.current;
		let needsPolling;
		//If polling is dependent on the individual order item statuses.
		if (dependentonItemStatuses) {
			const [pollingRequired, itemStatusPollingPeriod]: any = checkForActiveItems(status, orderItems);
			newPollingPeriod = itemStatusPollingPeriod || newPollingPeriod;
			needsPolling = pollingRequired || !!newPollingPeriod;
		} else {
			needsPolling = !!newPollingPeriod;
		}
		//If polling is needed and polling hasn't been started
		if (needsPolling && !pollingId.current) {
			pollingPeriod.current = newPollingPeriod;
			startPolling();
		}
		//If polling period is changed update polling
		else if (needsPolling && newPollingPeriod !== currentPollingPeriod) {
			pollingPeriod.current = newPollingPeriod;
			updatePollingPeriod();
		}
		//No polling period found stop polling.
		else if (!needsPolling && pollingId.current) {
			stopPolling();
		}
	}

	async function syncOrderDataAfterCollectionRequest() {
		stopPolling();
		await getOrderData(false, true);
	}

	/* Fetching Order Data */

	async function getOrderData(handlePageLoading = false, isPostCollectionSync = false) {

		if (handlePageLoading) {
			setLoading(true);
		}

		if(isPostCollectionSync) {
			setUpdateDataCallLoading(true);
		}

		const response = await OrderService.trackOrder(orderId, uuid);
		const { data, error, metadata } = response;
		if (data) {
			let receivedOrderData = data;
			//Setting the order data to state
			setOrderData(receivedOrderData);
			setLoading(false);

			if (consecutiveErrors.current) consecutiveErrors.current = 0;
		}
		if (error) {
			//This will be handled only while polling and not on inital call failure.

			if (metadata && errorMetaDataHandler) {
				errorMetaDataHandler(metadata);
			} else {
				if (consecutiveErrors.current !== maxRequestErrorsAllowed && pollingId.current) {
					//Directly incrementing a ref gives out weird behaviour for some reason. TODO: Investigate.
					consecutiveErrors.current = consecutiveErrors.current + 1;
				} else {
					setError(error);
					setLoading(false);
					stopPolling();
				}
			}
		}

		if(isPostCollectionSync) {
			setUpdateDataCallLoading(false);
		}

	}

	/* Render Functions */

	function renderOrderItems(orderItems: any, orderId: string, dietaryPreferences: any, collectTime:any) {
		let orderItemElements: any[] = [];
		orderItems.map((dishData: any, index: any) => {
			orderItemElements.push(
				<OrderItem
					orderItemData={dishData}
					dietaryPreferences={dietaryPreferences}
					orderId={orderId}
					itemIndex={index}
					syncOrderDataAfterCollectionRequest={syncOrderDataAfterCollectionRequest}
					updateDataCallLoading={updateDataCallLoading}
					uuid={uuid}
					errorMetaDataHandler={errorMetaDataHandler}
					collectTime={collectTime}
					toggleOverlay={toggleOverlay}
				/>
			);
		});
		return <CartItemsCard key={`${orderId}-cart`}>{orderItemElements}</CartItemsCard>;
	}

	function renderOrderData(orderData: any) {
		let renderData = [];
		const { orderId, status, orderItems, restaurantId, restaurantName, collectTime } = orderData;
		//Order Status Check
		if (!(status in orderStatusMap)) {
			console.error(`Order Status Invalid -- ${status}`);
			//Show Error Page
			return;
		}
		const brandDetails = orderItems?.[0]?.brandDetails?.[0];
		renderData.push(
			<>
				<PageContainer>
					<OrderDetails>
						<BrandLogo>
							<BrandLogoImg src={brandDetails?.brandCms?.brandLogo || ""} />
						</BrandLogo>
						<KioskDetailsContainer>
							<KioskDetails subText>{restaurantName}</KioskDetails>
							<KioskDetails>
								{brandDetails?.brandName}, Kiosk ID : {restaurantId}
							</KioskDetails>
						</KioskDetailsContainer>
					</OrderDetails>
					{renderOrderItems(orderItems, orderId, dietaryPreferences, collectTime)}
				</PageContainer>
			</>
		);

		return renderData;
	}

	function renderActiveOrderData(orderData: any) {
		if (!orderData) return;

		let renderData = [];

		const { status } = orderData;

		switch (status) {
			case OrderStatuses.CREATED:
			case OrderStatuses.IN_QUEUE:
			case OrderStatuses.IN_PROGRESS:
				renderData.push(renderOrderData(orderData));
				renderData.push(renderCollectionModal(orderData))
				break;

			case OrderStatuses.COMPLETED:
				renderData.push(renderOrderCompletedState(orderData));
				break;

			case OrderStatuses.PARTIALLY_COMPLETED:
				//NOTE : This is a very temporary change. Revert this ASAP.

				// renderData.push(renderPartiallyCompletedState(orderData));
				renderData.push(renderOrderCompletedState(orderData));
				break;

			case OrderStatuses.ABORTED:
			case OrderStatuses.FAILED:
				renderData.push(renderOrderAbortedState(orderData));
				break;

			default:
				break;
		}

		return renderData;
	}

	function renderOrderCompletedState(orderData: any) {
		const { orderId, restaurantId } = orderData;
		const { statusMessage, color } = orderStatusMap[OrderStatuses.COMPLETED];

		return (
			<>
				<CenteredContainer>
					<OrderDetails terminalState>
						<OrderId>Order {orderId}</OrderId>
					</OrderDetails>
					<OrderDetails terminalState>
						<div>
							<KioskDetails>
								Kiosk ID : {restaurantId} {}
							</KioskDetails>
						</div>
					</OrderDetails>

					<BigSpace />

					<StatusHead color={color}>{statusMessage}</StatusHead>
					<Icon version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
						<circle
							className="path circle"
							fill="none"
							stroke={color}
							stroke-width="6"
							stroke-miterlimit="10"
							cx="65.1"
							cy="65.1"
							r="62.1"
						/>
						<polyline
							className="path check"
							fill="none"
							stroke={color}
							stroke-width="6"
							stroke-linecap="round"
							stroke-miterlimit="10"
							points="100.2,40.2 51.5,88.8 29.8,67.5 "
						/>
					</Icon>

					<BigSpace />

					<Message>It was pleasure serving you. Hoping to see u order again soon...</Message>

					<SmallerSpace />

					<Link to={`/kiosk/${restaurantId}`}>Browse Menu</Link>
				</CenteredContainer>

				<div
					style={{ margin: "1.5rem 0 0 0", padding: "0 1rem" }}
					data-tf-live="01KEWCEX12B3SJ9KSP1S1T70XV"
					data-tf-hidden={`order_id=${orderId},src=app_details_page`}></div>
			</>
		);
	}

	function renderPartiallyCompletedState(orderData: any) {
		const { orderId, restaurantId } = orderData;
		const { statusMessage, color } = orderStatusMap[OrderStatuses.PARTIALLY_COMPLETED];

		return (
			<>
				<CenteredContainer>
					<OrderDetails terminalState>
						<OrderId>Order {orderId}</OrderId>
					</OrderDetails>
					<OrderDetails terminalState>
						<div>
							<KioskDetails>
								Kiosk ID : {restaurantId} {}
							</KioskDetails>
						</div>
					</OrderDetails>

					<BigSpace />

					<StatusHead color={color}>{statusMessage}</StatusHead>
					<Icon version="1.1" xmlns="http://www.w3.org/2000/svg">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							version="1.1"
							x="0px"
							y="0px"
							viewBox="0 0 256 256"
							enable-background="new 0 0 256 256">
							<g>
								<g>
									<g>
										<path
											stroke-width="2"
											fill={color}
											d="M116.9,10.3c-52.4,5-95.5,44.4-105,95.8c-1.4,7.8-1.8,12.6-1.8,21.9c0,19.9,4.1,36.8,13.1,54.2c5.9,11.4,12.4,20.4,21.5,29.4c9.5,9.5,19.3,16.5,30.7,22.2c26.2,12.9,54.2,15.8,82.7,8.5c8-2,13.8-4.2,22.2-8.4c29.1-14.2,51.5-40.5,60.9-71.5c5.6-18.6,6.5-39.7,2.5-59.1c-9.6-46.4-48.2-83.7-95-91.6C138,9.8,126.8,9.4,116.9,10.3z M137.7,25.1c48.5,4.7,87,42.4,93,91c0.6,5.3,0.6,18.5,0,23.7c-6.8,55.9-56.4,96.2-112.2,91c-41.1-3.7-76.4-32.1-89.1-71.7c-5.6-17.6-6.3-38.5-1.7-56.5c8.6-34.1,34.5-61.9,67.7-72.8C109.4,25.4,123.7,23.7,137.7,25.1z"
										/>
										<path
											fill={color}
											d="M117.1,87.4c0,12.2,0.1,14.4,2.5,38.8c1.4,14.2,2.5,26.1,2.5,26.3s2.6,0.4,5.8,0.4h5.7l0.2-1.5c0.1-0.8,1.3-12.5,2.6-25.9c2.2-22.9,2.3-25.3,2.3-37.8V74.4H128h-10.8L117.1,87.4L117.1,87.4z"
										/>
										<path stroke-width="6" fill={color} d="M118,170v10.2h9.9h9.9V170v-10.2H128H118V170z" />
									</g>
								</g>
							</g>
						</svg>
					</Icon>

					<BigSpace />

					<Message>
						We are sorry for the inconvenience caused. Rest assured we will be initiating a refund for the dishes that we couldn’t
						serve shortly.
					</Message>

					<BigSpace />

					<SmallMessage>You can track your refund status on your accounts page.</SmallMessage>
				</CenteredContainer>

				<div
					style={{ margin: "1.5rem 0 0 0", padding: "0 1rem" }}
					data-tf-live="01J515M78A7T3EF1NYQBKY0WHA"
					data-tf-hidden={`order_id=${orderId},src=app_details_page`}></div>
			</>
		);
	}

	function renderOrderAbortedState(orderData: any) {
		const { orderId, restaurantId } = orderData;
		const { statusMessage, color } = orderStatusMap[OrderStatuses.ABORTED];

		return (
			<>
				<CenteredContainer>
					<OrderDetails terminalState>
						<OrderId>Order {orderId}</OrderId>
					</OrderDetails>
					<OrderDetails terminalState>
						<div>
							<KioskDetails>Kiosk ID : {restaurantId} </KioskDetails>
						</div>
					</OrderDetails>

					<BigSpace />

					<StatusHead color={color}>{statusMessage}</StatusHead>
					<Icon version="1.1" xmlns="http://www.w3.org/2000/svg">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							version="1.1"
							x="0px"
							y="0px"
							viewBox="0 0 256 256"
							enable-background="new 0 0 256 256">
							<g>
								<g>
									<g>
										<path
											stroke-width="2"
											fill={color}
											d="M116.9,10.3c-52.4,5-95.5,44.4-105,95.8c-1.4,7.8-1.8,12.6-1.8,21.9c0,19.9,4.1,36.8,13.1,54.2c5.9,11.4,12.4,20.4,21.5,29.4c9.5,9.5,19.3,16.5,30.7,22.2c26.2,12.9,54.2,15.8,82.7,8.5c8-2,13.8-4.2,22.2-8.4c29.1-14.2,51.5-40.5,60.9-71.5c5.6-18.6,6.5-39.7,2.5-59.1c-9.6-46.4-48.2-83.7-95-91.6C138,9.8,126.8,9.4,116.9,10.3z M137.7,25.1c48.5,4.7,87,42.4,93,91c0.6,5.3,0.6,18.5,0,23.7c-6.8,55.9-56.4,96.2-112.2,91c-41.1-3.7-76.4-32.1-89.1-71.7c-5.6-17.6-6.3-38.5-1.7-56.5c8.6-34.1,34.5-61.9,67.7-72.8C109.4,25.4,123.7,23.7,137.7,25.1z"
										/>
										<path
											fill={color}
											d="M117.1,87.4c0,12.2,0.1,14.4,2.5,38.8c1.4,14.2,2.5,26.1,2.5,26.3s2.6,0.4,5.8,0.4h5.7l0.2-1.5c0.1-0.8,1.3-12.5,2.6-25.9c2.2-22.9,2.3-25.3,2.3-37.8V74.4H128h-10.8L117.1,87.4L117.1,87.4z"
										/>
										<path stroke-width="6" fill={color} d="M118,170v10.2h9.9h9.9V170v-10.2H128H118V170z" />
									</g>
								</g>
							</g>
						</svg>
					</Icon>

					<BigSpace />

					<Message>
						We are sorry for the inconvenience caused. Rest assured we will be initiating a refund for the dishes that we couldn’t
						serve shortly.
					</Message>

					<BigSpace />

					<SmallMessage>You can track your refund status on your accounts page.</SmallMessage>
				</CenteredContainer>

				<div
					style={{ margin: "1.5rem 0 0 0", padding: "0 1rem" }}
					data-tf-live="01J515M78A7T3EF1NYQBKY0WHA"
					data-tf-hidden={`order_id=${orderId},src=app_details_page`}></div>
			</>
		);
	}

	function renderCollectionModal(orderData:any) {
		if(!isEmptyObject(orderData)) {
			return <CollectionModal orderData={orderData}/>
		}
	}

	return (
		<Page padding="0 0 1rem 0">
			<ActiveOrderMessage orderData={orderData} />
			{!error ? <OrderItems>{renderActiveOrderData(orderData)}</OrderItems> : ""}
			{!loading && !error ? (
				<PageContainer>
					<ATHS />
				</PageContainer>
			) : (
				""
			)}
			{loading ? (
				<Center>
					<IonSpinner name="dots" color={"#F2F2F2"}></IonSpinner>
				</Center>
			) : (
				""
			)}
			{error ? (
				<Center>
					<ErrorComponent code={errorCode} />
				</Center>
			) : (
				""
			)}
			{showOverlay ? <ModalOverlay /> : ""}
		</Page>
	);
};

export default withNav(TrackOrder);
